/**
 * Rattraper le journal d'un jour oublié (web-pwa §5.6).
 *
 * Demandé par Hedi le 8 septembre 2026, le lendemain d'un soir où ni Baya ni
 * Mehdi n'avaient écrit le leur : *« laisser un bouton en bas dans ma journée
 * pour renseigner ce journal non fait »*. Le rituel du soir prévient désormais
 * le parent quand le journal manque ; il n'offrait à l'enfant aucun moyen de
 * combler le trou, et un manque qu'on ne peut plus combler cesse d'être un
 * manque pour devenir un reproche.
 *
 * **Un écran séparé, et non le journal du jour avec une date.** Il ne marque
 * rien comme envoyé pour aujourd'hui : le journal *d'aujourd'hui* reste à
 * écrire après un rattrapage, et les confondre ferait passer le mot d'un autre
 * jour pour celui de la soirée.
 *
 * **Les champs, eux, sont les mêmes** (`ChampsDuJournal`) : deux copies
 * auraient divergé, et l'enfant aurait vu un formulaire différent selon le jour
 * qu'il raconte, sans que rien ne dise lequel est juste.
 *
 * **La borne est au serveur.** La date est dans l'URL, donc choisie par le
 * client : `journal.rattrapable` refuse un jour à venir, un jour trop ancien et
 * un jour sans cours, et rend la phrase que cet écran affiche telle quelle. La
 * réinventer ici en produirait une autre — « trop loin » là où le serveur dit
 * « pas cours ce jour-là » —, et l'enfant chercherait une erreur qui n'existe
 * pas.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { JournalDuJour, LigneDeJournal } from "@/api/schema";
import { BoutonsDuJournal, ChampsDuJournal } from "@/child/JournalScreen";
import { newClientId } from "@/offline/outbox";
import { Card, ErrorNote, Loading, Screen } from "@/ui/primitives";

/** « 2026-09-07 » → « lundi 7 septembre ». */
const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
];

export function jourEnClair(iso: string): string {
  const [an, mois, jour] = iso.split("-").map(Number);
  if (!an || !mois || !jour) return iso;
  // Midi UTC : à minuit, le fuseau de Paris ferait basculer la date d'un jour
  // en arrière la moitié de l'année, et l'écran titrerait « dimanche » sur le
  // journal du lundi.
  const date = new Date(Date.UTC(an, mois - 1, jour, 12));
  return `${JOURS[date.getUTCDay()]} ${jour} ${MOIS[mois - 1]}`;
}

export function JournalRattrapageScreen() {
  const { le = "" } = useParams();
  const navigate = useNavigate();

  const [journal, setJournal] = useState<JournalDuJour | null>(null);
  const [lignes, setLignes] = useState<LigneDeJournal[]>([]);
  const [refus, setRefus] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  /** Idempotence (invariant §14.5), frappée au tap et gardée pour les réessais. */
  const envoiEnCours = useRef<string | null>(null);

  const charger = useCallback(async () => {
    setRefus(null);
    try {
      const recu = await call("monJournalOublie", { params: { le } });
      setJournal(recu);
      setLignes(recu.lignes ?? []);
    } catch (panne) {
      // **La raison vient du serveur et s'affiche telle quelle** — c'est la
      // règle d'`api/errors.ts` : « le serveur possède les mots que la famille
      // lit ». La réinventer ici en produirait une seconde, qui dirait « trop
      // loin » là où le serveur dit « pas cours ce jour-là ».
      setRefus(
        panne instanceof ApiError && panne.status === 422
          ? panne.message
          : "Je n'arrive pas à ouvrir ce journal-là.",
      );
    }
  }, [le]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const modifier = (rang: number, champ: "fait" | "coince", valeur: string) =>
    setLignes((etat) =>
      etat.map((ligne, index) => (index === rang ? { ...ligne, [champ]: valeur } : ligne)),
    );

  const remplies = lignes.filter((l) => (l.fait ?? "").trim() || (l.coince ?? "").trim());

  const envoyer = async (rienADire: boolean) => {
    setEnvoi(true);
    setErreur(null);
    envoiEnCours.current ??= newClientId();
    try {
      await call("enregistrerMonJournalOublie", {
        params: { le },
        body: {
          lignes: rienADire ? lignes.map((l) => ({ ...l, fait: "", coince: "" })) : lignes,
          nothing_to_say: rienADire,
          client_id: envoiEnCours.current,
        },
      });
      envoiEnCours.current = null;
      // **Retour à « Ma journée », et non au travail.** Le journal d'aujourd'hui
      // reste à écrire, et c'est là qu'il se propose.
      navigate("/eleve");
    } catch (panne) {
      setErreur(
        panne instanceof ApiError && panne.status === 422
          ? panne.message
          : navigator.onLine
          ? "Je n'ai pas réussi à envoyer ce journal. Réessaie dans un instant."
          : "Tu es hors ligne. Ton texte reste là ; réessaie quand ça revient.",
      );
    } finally {
      setEnvoi(false);
    }
  };

  if (refus) {
    return (
      <Screen title="Ce journal-là">
        <Card>
          <p>{refus}</p>
        </Card>
        <p className="aside">
          <Link to="/eleve">Revenir à ma journée</Link>
        </p>
      </Screen>
    );
  }

  if (!journal) return <Loading label="Je retrouve ce jour-là…" />;

  return (
    <Screen
      title={jourEnClair(le)}
      lead="Ce que tu avais oublié de raconter. Écris ce dont tu te souviens, rien de plus."
    >
      {/* **Ce qui est reconstitué de mémoire vaut moins qu'un journal du soir**,
          et l'enfant doit le savoir : inventer pour remplir ferait entrer dans
          la lecture de journée des notions que personne n'a enseignées. */}
      <p className="muted small">
        Si tu ne te rappelles plus, laisse vide — c'est mieux que d'inventer.
      </p>

      {journal.envoye ? (
        <p className="muted small">Tu avais déjà écrit quelque chose ce jour-là ; tu peux le corriger.</p>
      ) : null}

      <ChampsDuJournal journal={journal} lignes={lignes} modifier={modifier} />

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}

      {/* **La même confirmation qu'aujourd'hui, par le même composant.** Un
          jour reconstitué de mémoire est celui où « rien de spécial » est le
          plus probable et le plus juste ; l'en dispenser aurait fait deux
          gestes différents pour un même geste, et le second sans garde-fou. */}
      <Card>
        <BoutonsDuJournal
          envoyer={(rienADire) => void envoyer(rienADire)}
          envoi={envoi}
          remplies={remplies.length}
          quand="ce jour-là"
        />
      </Card>

      <p className="aside">
        <Link to="/eleve">Revenir à ma journée</Link>
      </p>
    </Screen>
  );
}
